"use client"

import { BlokProps, withStoryblokEditable } from "@helpers/StoryblokHelpers"
import SendIcon from "@layout/icons/SendIcon"
import SpinnerIcon from "@layout/icons/SpinnerIcon"
import { trackEvent } from "@lib/amplitude/amplitude"
import { sendBrazeRequest } from "@lib/sendBrazeRequest"
import { InterestSignupSectionStoryblok } from "@storyblok-types"
import ConfirmDialog from "@ui-library/dialog/ConfirmDialog"
import Richtext from "@ui-library/richtext/Richtext"
import { Paragraph } from "@ui-library/typography/Body"
import { H3 } from "@ui-library/typography/Headings"
import { cn } from "@utils/utils"
import { usePathname } from "next/navigation"
import { useState } from "react"

interface ApiResponse {
  status: boolean
  message?: string
}

type Message = {
  title: string
  content: string
  error: boolean
}

export const InterestSignupSection = ({ blok }: BlokProps<InterestSignupSectionStoryblok>) => {
  const pathname = usePathname()
  const [email, setEmail] = useState<string>("")
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [result, setResult] = useState<ApiResponse | null>(null)
  const [isSending, setIsSending] = useState<boolean>(false)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [message, setMessage] = useState<Message>({ title: "", content: "", error: false })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsSending(true)
    e.preventDefault()
    const response = await sendBrazeRequest(email, { tag: blok.braze_tag })
    setResult(response)
    setIsSending(false)

    if (response.status) {
      setMessage({
        title: blok.success_title || "Tack!",
        content: blok.success_message || "Din intresseanmälan är mottagen.",
        error: false
      })
      trackEvent("interest.signup.succeeded", { tag: blok.braze_tag, page_path: pathname })
      setOpenDialog(true)
    }
  }

  return (
    <div
      className="mt-8 rounded-2xl bg-secondary-10 px-4 py-12 text-center text-on-secondary-10 lg:mt-16"
      {...withStoryblokEditable(blok)}
    >
      <form className="mx-auto flex max-w-md flex-col" onSubmit={handleSubmit}>
        {blok.title && <H3>{blok.title}</H3>}
        {blok.content && <Richtext bodySmall content={blok.content} className="mb-6" />}

        <div className="relative flex items-center justify-between">
          <input
            type="email"
            id="interest-email"
            placeholder={blok.placeholder}
            value={email}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
            onChange={e => setEmail(e.target.value)}
            disabled={isSending}
            required
            className={cn(
              "h-12 w-full rounded-md border border-on-surface bg-surface px-4 text-on-surface outline-none transition-all",
              isSending && "pointer-events-none text-neutral-50",
              isFocus && "border-on-secondary-10 shadow-inputInner",
              result && !result.status && "border-warning-50"
            )}
          />
          <button
            type="submit"
            className="absolute right-4 text-on-surface"
            disabled={isSending}
            aria-label={blok.button_label || "Skicka intresseanmälan"}
          >
            {isSending ? <SpinnerIcon className="h-8 w-8 animate-spin" /> : <SendIcon />}
          </button>
        </div>

        {result && (
          <Paragraph size="small" className={cn("w-full pt-2 text-left", !result.status && "text-warning-50")} asChild>
            <span>{!result.status && <>{result.message}</>}</span>
          </Paragraph>
        )}

        {blok.policy && (
          <Paragraph size="xsmall" className="mt-4" asChild>
            <span>{blok.policy}</span>
          </Paragraph>
        )}
      </form>

      <ConfirmDialog
        title={message.title}
        message={message.content}
        error={message.error}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />
    </div>
  )
}

export default InterestSignupSection
